import { Box, Button, Chip, Divider, FormControl, FormControlLabel, InputBase, LinearProgress, linearProgressClasses } from "@mui/material"
import { alpha, styled } from "@mui/system"
import { DataGrid } from "@mui/x-data-grid"
import { SIDEBAR_WIDTH, SIDEBAR_WIDTH_COLLAPSED } from "../../../utilities/constants"
import { getReplaneConfig } from "../../../utils/replaneClient"

export const UiCtButton = styled(Button, {
  name: "ChaosTrackButton",
})(({ theme, variant }) => ({
  borderRadius: "10px",
  fontSize: "1rem",
  fontWeight: 500,
  fontStyle: "normal",
  lineHeight: "normal",
  textTransform: "none",
  ...(variant === "contained"
    ? {
        backgroundColor: theme.palette.primary.solidBg,
        color: theme.palette.primary.solidColor,
        "&:hover": {
          backgroundColor: theme.palette.primary.solidHoverBg,
        },
        "&:active": {
          backgroundColor: theme.palette.primary.solidActiveBg,
        },
        "&:disabled": {
          backgroundColor: theme.palette.primary.solidDisabledBg,
          color: theme.palette.primary.solidDisabledColor,
        },
      }
    : variant === "outlined"
      ? {
          backgroundColor: theme.palette.primary.outlinedActiveBg,
          color: theme.palette.primary.outlinedColor,
          border: `1px solid ${theme.palette.primary.outlinedBorder}`,
          "&:hover": {
            backgroundColor: theme.palette.primary.outlinedHoverBg,
            color: theme.palette.primary.softActiveColor,
          },
          "&:active": {
            backgroundColor: theme.palette.primary.outlinedActiveBg,
          },
          "&:disabled": {
            borderColor: theme.palette.primary.outlinedDisabledBorder,
            color: theme.palette.primary.outlinedDisabledColor,
          },
        }
      : {}),
}))

export const UiCtDivider = styled(Divider, {
  name: "ChaosTrackDivider",
})(({ theme }) => ({
  color: theme.palette.primary.main,
  "&::before, &::after": {
    borderColor: theme.palette.primary.main,
    borderWidth: "1px",
  },
}))

export const UiCtFormControl = styled((props) => <FormControl {...props} />, {
  name: "ChaosTrackFormControl",
})(({ theme }) => ({
  borderWidth: "1px",
  borderStyle: "solid",
  borderColor: "transparent",
  "&.is-invalid": {
    borderColor: theme.palette.error.main,
  },
  "&:not(.is-invalid):focus-within": {
    borderColor: theme.palette.primary.main,
  },
}))

export const UiCtFormControlLabel = styled((props) => <FormControlLabel {...props} />, {
  name: "ChaosTrackFormControlLabel",
})(({ theme }) => ({
  ".MuiFormControlLabel-label": {
    color: theme.palette.text.primary.main,
  },
}))

export const UiCtDataGrid = styled(DataGrid, {
  name: "ChaosTrackDataGrid",
})(({ theme, rowBg }) => ({
  color: theme.palette.text.primary,
  borderStyle: "hidden",
  borderRadius: 15,
  "& .MuiDataGrid-main": {
    minHeight: "300px",
    "--DataGrid-overlayHeight": "300px",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: theme.palette.background.surface,
    borderBottom: "none",
    borderRadius: 10,
  },
  "& .MuiDataGrid-row": {
    backgroundColor: theme.palette.background[`${rowBg || "surface"}`],
    borderBottom: "none",
    borderRadius: 10,
    "&.Mui-hovered": {
      boxShadow: `0px 0px 12px ${theme.palette.background.backdrop}`,
      outline: `1px solid ${theme.palette.primary.outlinedBorder}`,
      "& .MuiDataGrid-cell:first-child": {
        borderRadius: 10,
        borderLeft: `1px solid ${theme.palette.primary.outlinedBorder}`,
      },
      "& .MuiDataGrid-cell:last-child": {
        borderRadius: 10,
        borderRight: `1px solid ${theme.palette.primary.outlinedBorder}`,
      },
    },
  },
  "& .MuiDataGrid-cell": {
    borderBottom: "none",
    "&:focus": {
      outline: "none",
    },
    "&:focus-within": {
      outline: "none",
    },
  },
}))

export const UiCtInputBase = styled(InputBase, {
  name: "ChaosTrackInputBase",
})(({ theme, rowBg }) => ({
  backgroundColor: theme.palette.background.surface,
}))

// Rail width in px for the current state (0 when the rail is hidden).
const railWidth = (visible, expanded) =>
  visible
    ? expanded
      ? Number(getReplaneConfig("frontend", "ui.sidebar_width", SIDEBAR_WIDTH))
      : Number(getReplaneConfig("frontend", "ui.sidebar_width_collapsed", SIDEBAR_WIDTH_COLLAPSED))
    : 0

// Height of the sticky RoleAssumptionBanner above the layout (set by the banner; 0 when hidden).
const BANNER_OFFSET = "var(--ct-banner-h, 0px)"

// The rail animates its width; the content's offset animates with the same timing.
const RAIL_TRANSITION_MS = 300
const RAIL_TRANSITION_CONTENT = `margin-left ${RAIL_TRANSITION_MS}ms ease, max-width ${RAIL_TRANSITION_MS}ms ease`

// At or below this window width the content column scrolls its own horizontal overflow.
export const NARROW_LAYOUT_MAX_PX = 768

// CT Modern chrome: theme-aware rail (--ct-sidebar — white in light, navy in
// dark), tinted active state, section labels; main content sits on the cohesive
// --ct-canvas surface. Sizing/visibility/toggle logic unchanged.
export const UiLayoutContainer = styled(Box, {
  name: "ChaosTrackLayoutContainer",
})(({ theme, visible, expanded }) => ({
  display: "flex",
  flexDirection: "row",
  fontFamily: "var(--ct-font-sans)",
  width: "100%",

  "& .layout-sidepanel": {
    position: "relative",
    minWidth: visible
      ? expanded
        ? `${getReplaneConfig("frontend", "ui.sidebar_width", SIDEBAR_WIDTH)}px`
        : `${getReplaneConfig("frontend", "ui.sidebar_width_collapsed", SIDEBAR_WIDTH_COLLAPSED)}px`
      : 0,
    // Animates now that the pinned app rail sets an explicit width (see "& > .layout-sidepanel").
    transition: `width ${RAIL_TRANSITION_MS}ms ease`,
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    borderRight: "1px solid var(--ct-sidebar-line)",
    backgroundColor: visible ? "var(--ct-sidebar)" : "transparent",
    display: visible ? "block" : "none",
    boxSizing: "border-box",
    height: "100vh",
    // visible so the account drop-up can escape the rail (the width-collapse
    // transition this hid content for was already non-functional — see TODO above)
    overflow: "visible",
    zIndex: 3,

    // Brand block at the top of the rail: org logo + persisted theme toggle
    "& .side-panel-brand": {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 9,
      // Flattened spec: no divider under the brand; quiet padding
      padding: "16px 12px 18px",
      "&.collapsed": {
        justifyContent: "center",
        padding: "16px 6px 18px",
      },
      "& .side-panel-logo-link": {
        display: "inline-flex",
        alignItems: "center",
        minWidth: 0,
      },
      "& .side-panel-logo": {
        height: 34,
        maxWidth: 150,
        width: "auto",
        objectFit: "contain",
        // Org-uploaded logos vary; keep the white chip so transparent logos stay
        // readable on the navy rail (mirrors the old header treatment)
        "&.side-panel-logo--custom": {
          height: 40,
          padding: 2,
          background: "#fff",
          borderRadius: 6,
          boxSizing: "border-box",
        },
      },
      "& .side-panel-theme-toggle": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        flex: "0 0 32px",
        padding: 0,
        borderRadius: 9,
        border: "1px solid var(--ct-sidebar-line)",
        background: "var(--ct-sidebar-hover)",
        color: "var(--ct-sidebar-ink)",
        fontSize: 13,
        cursor: "pointer",
        "&:hover": {
          color: "var(--ct-gold)",
          borderColor: "color-mix(in srgb, var(--ct-gold) 50%, transparent)",
        },
      },
    },

    "& .side-panel-list": {
      "& .side-panel-section-label": {
        padding: "12px 10px 5px",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "var(--ct-sidebar-muted)",
        listStyle: "none",
        userSelect: "none",
      },
      "& .MuiListItem-root": {
        display: "block",
        padding: "0 10px",
        "& .MuiListItemButton-root": {
          borderRadius: 8,
          color: "var(--ct-sidebar-ink)",
          "&:hover": {
            backgroundColor: "var(--ct-sidebar-hover)",
            color: "var(--ct-sidebar-active-ink)",
          },
        },
        "&:has(.list-button.Mui-disabled)": {
          cursor: "not-allowed",
          backgroundColor: "transparent",
        },
        // Flattened spec: active = navactive tint + 3px inset accent LEFT edge +
        // accent icon + 600 weight. No border, no right-side indicator.
        "& .active": {
          backgroundColor: "var(--ct-sidebar-active-bg)",
          color: "var(--ct-sidebar-active-ink)",
          // Fixed brand orange (not var(--ct-accent), which flips to gold in the
          // dark app theme) so the always-navy rail reads consistently.
          boxShadow: "inset 3px 0 0 #e8890c",
          ".active-indicator": {
            display: "none",
          },
        },
        "& .active .MuiListItemIcon-root": {
          color: "#e8890c",
        },
        "& .list-button": {
          padding: "9px 10px",
          minHeight: 0,
        },
        "& .MuiListItemIcon-root": {
          minWidth: 0,
          marginRight: 10,
          color: "inherit",
          fontSize: "1rem",
        },
        "& .MuiListItemText-root": {
          margin: 0,
          "& .MuiTypography-root": {
            fontFamily: "var(--ct-font-sans)",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: "normal",
          },
        },
        "& .active .MuiListItemText-root .MuiTypography-root": {
          fontWeight: 600,
        },
      },
    },
    "& .MuiDivider-root": {
      borderWidth: 1,
      borderColor: "var(--ct-sidebar-line)",
      margin: theme.spacing(1, 2),
    },
    // Account menu pinned to the rail footer (drop-up)
    "& .user-menu-footer": {
      borderTop: "1px solid var(--ct-sidebar-line)",
      padding: "10px 10px",
      "& .user-menu-trigger": {
        display: "flex",
        alignItems: "center",
        gap: 10,
        width: "100%",
        // Neutralize the legacy dark-only `.btn { min-width: 100px }` (theme-dark.scss),
        // which otherwise inflated the COLLAPSED rail to ~110px in dark mode only — the rail
        // must be the same width (and icons centered) in both themes.
        minWidth: 0,
        // Leave room for the collapse chevron on the rail's right edge so the
        // account text never sits under it.
        paddingRight: 12,
        textAlign: "left",
        color: "var(--ct-sidebar-ink)",
        border: 0,
        "&::after": { display: "none" },
        "&:hover": { color: "var(--ct-sidebar-active-ink)" },
        "& .profile-image": { flex: "0 0 auto", border: "2px solid var(--ct-sidebar-line)" },
        "& .user-menu-id": {
          display: "flex",
          flexDirection: "column",
          // Flex child must be allowed to shrink below its content width, else the
          // email overflows and clips mid-text instead of showing an ellipsis.
          flex: "1 1 auto",
          minWidth: 0,
          "& .user-menu-email": {
            fontSize: 12.5,
            fontWeight: 600,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .user-menu-org": {
            fontSize: 11,
            color: "var(--ct-sidebar-muted)",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
        },
      },
      "& .dropdown-menu": {
        backgroundColor: "var(--ct-surface)",
        border: "1px solid var(--ct-line)",
        boxShadow: "var(--ct-shadow)",
        // Expanded rail: pin the drop-up to the rail width. Collapsed rail (~64px): the
        // panel can't fit — give it a real width and let it fly out to the right over the
        // content (the rail is overflow:visible), so the account details are never clipped.
        width: expanded ? "100%" : 260,
        minWidth: expanded ? 0 : 260,
        boxSizing: "border-box",
        overflow: "hidden",
        "& .dropdown-item": {
          color: "var(--ct-ink-2)",
          "&:hover": { backgroundColor: "var(--ct-surface-2)", color: "var(--ct-ink)" },
        },
        "& .dropdown-user-summary": {
          borderBottom: "1px solid var(--ct-line)",
          "& .dropdown-user-name": { fontSize: "0.85rem", fontWeight: 600, color: "var(--ct-ink)" },
          "& .dropdown-user-email": {
            fontSize: "0.75rem",
            color: "var(--ct-ink-3)",
            marginBottom: 6,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          },
          "& .dropdown-user-table": {
            fontSize: "0.78rem",
            color: "var(--ct-ink-2)",
            lineHeight: 1.8,
            "& .label-cell": { paddingRight: 12, color: "var(--ct-ink-3)" },
          },
        },
        "& .dropdown-secondary-status": { color: "var(--ct-ink-3)" },
      },
    },
  },
  // The app rail is pinned to the window: it always fills the window height (below the role-
  // assumption banner, if shown) and stays put while a long page (People, Dashboard) scrolls the
  // document — previously it was a 100vh in-flow box, so the rail ended partway down tall pages.
  // Direct child only: the wizard/builder rails reuse .layout-sidepanel inside their own layouts.
  // The main content is offset by the rail's width (marginLeft); the nav list itself scrolls on
  // short windows (SidePanel), and overflow stays visible so the account drop-up can escape.
  "& > .layout-sidepanel": {
    position: "fixed",
    top: BANNER_OFFSET,
    left: 0,
    height: `calc(100vh - ${BANNER_OFFSET})`,
    width: `${railWidth(visible, expanded)}px`,
  },
  "& .layout-main-content": {
    flexGrow: 1,
    padding: theme.spacing(2, 3),
    // Fills the window below the banner (was a flat 100vh, which overflowed by the banner's height).
    height: `calc(100vh - ${BANNER_OFFSET})`,
    marginLeft: `${railWidth(visible, expanded)}px`,
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: "var(--ct-canvas)",
    borderRadius: 0,
    maxWidth: visible ? `calc(100% - ${railWidth(visible, expanded)}px)` : "100%",
    // Moves in step with the rail's width transition, so collapsing/expanding never overlaps or gaps.
    transition: RAIL_TRANSITION_CONTENT,
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
    // Narrow windows: the rail is pinned and the content offset by it, so content wider than the
    // window (a wide table) must not widen the document — a horizontal page scroll would slide it
    // UNDER the pinned rail. The content column holds its own overflow and scrolls instead (only
    // with the rail shown: embedded/printable layouts have no rail and keep the document scroll).
    ...(visible ? { [`@media (max-width: ${NARROW_LAYOUT_MAX_PX}px)`]: { overflow: "auto" } } : {}),
    // Print: no rail, so no offset and the full page width. Height auto: a window-high box with
    // a long report inside makes Firefox print one blank page per overflowed window height.
    "@media print": { marginLeft: 0, maxWidth: "100%", overflow: "visible", height: "auto", minHeight: 0 },
  },
  "& .layout-toggle-button": {
    position: "absolute",
    // Anchored to the bottom-right of the rail, lifted clear ABOVE the account
    // footer (avatar + email + org) so it never covers the login info, and inset
    // from the right edge so it doesn't sit on the rail's right border line.
    bottom: 88,
    right: 12,
    // A neat, evenly-sized round control. (Previously scaleX(0.75) horizontally
    // squished the chevron — fixed to a proper 30px circle.)
    minWidth: 30,
    width: 30,
    height: 30,
    padding: 0,
    borderRadius: "50%",
    zIndex: 4,
    // Brand orange, fixed regardless of app theme (the navy rail is always dark,
    // so the button must not flip to the dark-theme gold accent).
    backgroundColor: "#e8890c",
    color: "#ffffff",
    "&:hover": { backgroundColor: "#cf7607" },
  },
}))

export const UiChip = styled(Chip, {
  name: "ChaosTrackChip",
})(({ theme, variant }) => ({
  borderRadius: 8,
  fontSize: 14,
  backgroundColor: variant === "filled" ? theme.palette.primary.main : variant === "outlined" ? theme.palette.background.surface : "transparent",
  border: variant === "outlined" ? `1px solid ${theme.palette.primary.outlinedActiveBg}` : "none",
  boxShadow: theme.shadows[2],
}))

export const UiVisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
})

export const UiEditorBox = styled(Box, {
  name: "ChaosTrackEditorBox",
})(({ theme }) => ({
  pageBreakInside: "auto",
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.surface,
  boxShadow: theme.shadows[1],

  // Control group styles
  "& .control-group": {
    display: "flex",
    gap: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },

  // Button group styles
  "& .MuiButtonGroup-root.menu-btn-group": {
    marginBottom: theme.spacing(1),
    border: "none",
    "& .MuiButton-root": {
      color: theme.palette.text.secondary,
      border: "none",
      borderRadius: 4,
      padding: "3px 0 3px 8px",
      "&:hover": {
        borderColor: "transparent",
      },
      "&:disabled": {
        color: theme.palette.text.disabled,
        opacity: 0.5,
        cursor: "not-allowed",
      },
      "&.is-active": {
        // text.solid is the on-CTA color (white in light) — unreadable on level3.
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.level3,
      },
    },
  },

  // Editor container styles
  "& .editor-container": {
    minHeight: 200,
    maxHeight: 400,
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    backgroundColor: theme.palette.background.level1,

    "& p.ct-paragraph": {
      marginBottom: theme.spacing(1),
    },
    "& .ct-heading": {
      margin: theme.spacing(1, 0),
      color: theme.palette.text.primary,
    },
    "& h1.ct-heading": {
      fontSize: "1.5rem",
      fontWeight: 600,
    },
    "& h2.ct-heading": {
      fontSize: "1.25rem",
      fontWeight: 600,
    },
    "& h3.ct-heading": {
      fontSize: "1.1rem",
      fontWeight: 600,
    },
    "& h4.ct-heading": {
      fontSize: "1rem",
      fontWeight: 600,
    },
    "& .ct-code-inline": {
      backgroundColor: theme.palette.background.level2,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(0.4, 0.8),
      fontFamily: "Source Code Pro, monospace",
      color: theme.palette.primary.main,
    },
    "& .ct-code-block": {
      backgroundColor: theme.palette.background.level2,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(1),
      margin: theme.spacing(1, 0),
      fontFamily: "Source Code Pro, monospace",
      fontSize: "0.9rem",
      color: theme.palette.text.primary,
      overflowX: "auto",
    },
    "& .ct-blockquote": {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      margin: theme.spacing(1, 0),
      padding: theme.spacing(0.5, 1, 0.5, 2),
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.text.primary,
    },
    "& .ct-table": {
      width: "auto",
      borderCollapse: "collapse",
      margin: theme.spacing(1, 0),
      "& th, & td": {
        border: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(0.7, 1),
        textAlign: "left",
        "&>p": {
          margin: 0,
        },
      },
      "& th": {
        backgroundColor: theme.palette.background.level2,
      },
      "& tr:nth-of-type(even)": {
        backgroundColor: theme.palette.background.level1,
      },
    },

    // ProseMirror styles
    "& .ProseMirror": {
      minHeight: "100%",
      padding: theme.spacing(1),
      color: theme.palette.text.primary,
      // Text selection: a SOFT accent tint that keeps the text fully readable. Was
      // theme.palette.primary.light (the brand orange), which painted a heavy dark-orange
      // slab over selected text. Cover descendant text too (headings, table cells) so a
      // whole-document selection reads consistently. (border-radius is invalid on ::selection.)
      "&::selection, & ::selection": {
        backgroundColor: "rgba(232, 137, 12, 0.22)",
        color: "inherit",
      },
      // prosemirror-tables marks drag-selected cells with .selectedCell but ships no styles;
      // without this, selecting cells (how you delete a column) gives no feedback.
      "& .selectedCell": {
        position: "relative",
      },
      "& .selectedCell:after": {
        content: '""',
        position: "absolute",
        inset: 0,
        backgroundColor: alpha(theme.palette.primary.main, 0.18),
        pointerEvents: "none",
      },
    },
    "& .ProseMirror-focused": {
      outline: "none",
    },
    "& .ProseMirror-placeholder": {
      color: theme.palette.text.secondary,
    },
    "& .ProseMirror-selectednode": {
      backgroundColor: theme.palette.primary.light,
      borderRadius: theme.shape.borderRadius,
    },
  },
}))

export const UiCtProgressBar = styled(LinearProgress, {
  name: "ChaosTrackProgressBar",
})(({ theme, color }) => ({
  height: 12,
  borderRadius: 5,
  backgroundColor: theme.palette.background.level2,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    backgroundColor: color || theme.palette.primary.main,
  },
}))
